import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  // NOTE: We use Bearer token auth instead of credentials/cookies.
  // The preview platform's ingress injects a wildcard ACAO header on OPTIONS,
  // which breaks `withCredentials: true`. Bearer token in Authorization header
  // works on simple requests without preflight (when only standard headers used).
  withCredentials: false,
  // Iter 44 §P2 — Default timeout. Most API calls finish under 1s; the
  // 30s cap catches stuck connections without strangling slower paths
  // (PDF uploads, raster rendering). Long-poll callers should override
  // explicitly via `{ timeout: 120000 }` per-request — see
  // IngestionReview.jsx where heavy OCR can push individual GETs to
  // 30-60s during deep regional-OCR fallback.
  timeout: 30000,
});

// Bearer fallback (also lets <img>/<embed> URLs include a token via query)
let bearer = null;
export function setBearer(token) {
  bearer = token;
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    try { localStorage.setItem("fop_bearer", token); } catch {}
  } else {
    delete api.defaults.headers.common["Authorization"];
    try { localStorage.removeItem("fop_bearer"); } catch {}
  }
}

export function getBearer() {
  if (bearer) return bearer;
  try {
    const t = localStorage.getItem("fop_bearer");
    if (t) {
      bearer = t;
      api.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    }
    return bearer;
  } catch {
    return null;
  }
}

// FP-005 (2026-09-09 audit remediation) — the frontend used to discard the
// refresh_token returned by every login-shaped response entirely, so a
// field session silently died the instant the short-lived access token
// expired, with no way to recover short of a full re-login. Now persisted
// alongside the access token and used below to transparently refresh once
// on a 401 instead of dead-ending the user's session.
export function setRefreshToken(token) {
  if (token) {
    try { localStorage.setItem("fop_refresh", token); } catch {}
  } else {
    try { localStorage.removeItem("fop_refresh"); } catch {}
  }
}

export function getRefreshToken() {
  try { return localStorage.getItem("fop_refresh"); } catch { return null; }
}

function clearSession() {
  setBearer(null);
  setRefreshToken(null);
  // 2026-09-14 (ForgedOps completion) — this used to only clear the
  // localStorage tokens. Nothing told React's auth state that the
  // session had died, so `AuthContext.user` stayed truthy and
  // `ProtectedRoute` kept rendering the current protected page —
  // every background poller on it (SyncBadge, NotificationsBell,
  // OperationalStatusSpine, SheetStatusBar, OfflineQueuePanel,
  // SyncSuccessListener, IngestionReview's own batch poll, ...) kept
  // firing on its interval forever, each hitting a real 401 with no
  // token left to retry with, indefinitely, with no redirect to
  // /login. Dispatching this event lets `AuthContext` react
  // immediately: set `user=false`, which makes `ProtectedRoute`
  // render `<Navigate to="/login">`, which unmounts every child
  // (including all the pollers) in the same render pass — no more
  // requests fire after this point, and the user sees a real
  // "please sign in again" screen instead of a silently-stuck page.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("fop:session-expired"));
  }
}

// Serializes concurrent refresh attempts — several requests can 401 at
// once (e.g. a batch of parallel GETs right as the access token expires);
// only the FIRST triggers a real /auth/refresh call, the rest await the
// same in-flight promise instead of each minting their own refresh (which
// would rotate the refresh token out from under one another).
let refreshInFlight = null;
async function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const rt = getRefreshToken();
      if (!rt) throw new Error("No refresh token");
      const { data } = await axios.post(`${API_BASE}/auth/refresh`, null, {
        headers: { "X-Refresh-Token": rt },
      });
      setBearer(data.access_token);
      return data.access_token;
    })().finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

// Endpoints where a 401 must NOT trigger a refresh attempt — refreshing
// in response to a failed login/refresh/MFA-verify itself would either
// loop or refresh using a stale/foreign token.
const NO_REFRESH_RETRY = ["/auth/login", "/auth/refresh", "/auth/mfa/verify", "/auth/register"];

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    const isExempt = cfg && NO_REFRESH_RETRY.some((p) => cfg.url?.includes(p));
    if (status === 401 && cfg && !cfg._retriedAfterRefresh && !isExempt && getRefreshToken()) {
      cfg._retriedAfterRefresh = true;
      try {
        const newToken = await refreshAccessToken();
        cfg.headers = { ...cfg.headers, Authorization: `Bearer ${newToken}` };
        return api(cfg);
      } catch {
        // Refresh itself failed (revoked/expired/disabled) — the session
        // really is over. Clear local tokens so the next /auth/me check
        // (or any ProtectedRoute) correctly routes to /login instead of
        // looping on a dead access token forever.
        clearSession();
      }
    }
    return Promise.reject(error);
  },
);


// Initialize on module load
getBearer();

export function fileUrl(relPath) {
  // For sheet files / photos, attach token so browsers without cookies can fetch
  const t = getBearer();
  const sep = relPath.includes("?") ? "&" : "?";
  return `${API_BASE}/${relPath}${t ? `${sep}token=${t}` : ""}`;
}

export function sheetFileUrl(sheetId) {
  const t = getBearer();
  return `${API_BASE}/sheets/${sheetId}/file${t ? `?token=${t}` : ""}`;
}

export function sheetThumbUrl(sheetId) {
  const t = getBearer();
  return `${API_BASE}/sheets/${sheetId}/thumb${t ? `?token=${t}` : ""}`;
}

export function sheetRasterUrl(sheetId, opts = {}) {
  const t = getBearer();
  // Auto-detect retina by default — serves a 2× raster on iPad Pro / 4K
  // monitors for crisp plans. Pass {dpr:1} to force standard res (used by
  // RevisionCompare overlay where matching pixel dims between revisions is
  // critical, and for the offline service-worker cache to keep payloads
  // small). Pass {dpr:4} to request the 9600px deep-zoom ultra raster — the
  // TileManager opts into this when the user zooms past ~1.8× fit.
  let dpr;
  if (typeof opts.dpr === "number") {
    dpr = opts.dpr;
  } else if (typeof window !== "undefined" && window.devicePixelRatio >= 1.5) {
    dpr = 2;
  } else {
    dpr = 1;
  }
  // Backend clamps to {1, 2, 4}. Anything else gets normalised at the edge.
  let dprParam = "";
  if (dpr >= 4) dprParam = "&dpr=4";
  else if (dpr >= 2) dprParam = "&dpr=2";
  return `${API_BASE}/sheets/${sheetId}/raster${t ? `?token=${t}${dprParam}` : ""}`;
}

export function photoUrl(relPath) {
  const t = getBearer();
  // relPath comes back as "photos/{tenant}/xxx.jpg" — server route is /api/photos/{filepath:path}
  const clean = relPath.startsWith("photos/") ? relPath.substring(7) : relPath;
  return `${API_BASE}/photos/${clean}${t ? `?token=${t}` : ""}`;
}

/** Iter 35 — full-size photo binary keyed by photo_id (NOT a storage path).
 *  The endpoint is `/api/photos/{photo_id}/file` and is tenant-isolated. */
export function photoFileUrl(photoId) {
  const t = getBearer();
  return `${API_BASE}/photos/${photoId}/file${t ? `?token=${t}` : ""}`;
}

/** Iter 35 — server-generated 320px thumbnail. Cheap enough to render
 *  inline in every Inspector timeline; cached `private, max-age=86400`. */
export function photoThumbUrl(photoId) {
  const t = getBearer();
  return `${API_BASE}/photos/${photoId}/thumb${t ? `?token=${t}` : ""}`;
}

/** Resolve a tenant.branding.logo_url (now served via the PUBLIC,
 *  unauthenticated /api/tenant-logo/{tenant_id}/{filename} route — FP-002
 *  audit remediation, 2026-09-09) to a fetchable absolute URL. No bearer
 *  token is appended: logos must never carry a live session token into
 *  a URL that can end up in access logs, browser history, exported
 *  PDFs, emails or public share links. Returns "" when no logo set. */
export function brandedLogoUrl(logoUrl) {
  if (!logoUrl) return "";
  return logoUrl.startsWith("http") ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`;
}
